import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import "./TabsNavigation.scss";

// Margen para evitar que un desbordamiento de 1px (redondeos del navegador)
// haga aparecer las flechas cuando en realidad caben todas las pestañas.
const OVERFLOW_TOLERANCIA = 2;

const TabsNavigation = ({
    tabs = [],
    activeTab,
    onTabChange,
    className = "",
    noBorderRadius = false,
}) => {
    const listRef = useRef(null);
    const [puedeScrollIzquierda, setPuedeScrollIzquierda] = useState(false);
    const [puedeScrollDerecha, setPuedeScrollDerecha] = useState(false);

    const actualizarFlechas = useCallback(() => {
        const list = listRef.current;
        if (!list) return;

        const maxScroll = list.scrollWidth - list.clientWidth;
        setPuedeScrollIzquierda(list.scrollLeft > OVERFLOW_TOLERANCIA);
        setPuedeScrollDerecha(list.scrollLeft < maxScroll - OVERFLOW_TOLERANCIA);
    }, []);

    // Las flechas dependen del ancho disponible, no solo del de la ventana:
    // la barra vive dentro de tarjetas que cambian de tamaño por su cuenta.
    useLayoutEffect(() => {
        const list = listRef.current;
        if (!list) return;

        actualizarFlechas();

        const observer = new ResizeObserver(actualizarFlechas);
        observer.observe(list);
        return () => observer.disconnect();
    }, [actualizarFlechas, tabs]);

    // La pestaña activa puede venir de la URL (?tab=visitas) y quedar fuera de
    // la parte visible, así que la traemos a la vista.
    useEffect(() => {
        const list = listRef.current;
        if (!list) return;

        const botonActivo = list.querySelector(
            ".tabs-navigation__button--active"
        );
        if (botonActivo) {
            botonActivo.scrollIntoView({
                block: "nearest",
                inline: "nearest",
            });
        }
        actualizarFlechas();
    }, [activeTab, actualizarFlechas]);

    const scrollPor = (direccion) => {
        const list = listRef.current;
        if (!list) return;

        list.scrollBy({
            left: direccion * Math.max(list.clientWidth * 0.6, 120),
            behavior: "smooth",
        });
    };

    return (
        <nav
            className={`tabs-navigation ${
                noBorderRadius ? "tabs-navigation--no-radius" : ""
            } ${className}`}
        >
            {puedeScrollIzquierda && (
                <button
                    className="tabs-navigation__scroll tabs-navigation__scroll--left"
                    onClick={() => scrollPor(-1)}
                    type="button"
                    tabIndex={-1}
                    aria-hidden="true"
                >
                    <FiChevronLeft size={18} />
                </button>
            )}

            <ul
                className="tabs-navigation__list"
                ref={listRef}
                onScroll={actualizarFlechas}
            >
                {tabs.map((tab, index) => (
                    <li key={tab.id || index} className="tabs-navigation__item">
                        <button
                            className={`tabs-navigation__button ${
                                activeTab === tab.id
                                    ? "tabs-navigation__button--active"
                                    : ""
                            }`}
                            onClick={() => onTabChange(tab.id)}
                            type="button"
                        >
                            {tab.label}
                        </button>
                    </li>
                ))}
            </ul>

            {puedeScrollDerecha && (
                <button
                    className="tabs-navigation__scroll tabs-navigation__scroll--right"
                    onClick={() => scrollPor(1)}
                    type="button"
                    tabIndex={-1}
                    aria-hidden="true"
                >
                    <FiChevronRight size={18} />
                </button>
            )}
        </nav>
    );
};

export default TabsNavigation;
